import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ReactComponent as PauseIcon } from '../../../assets/icons/pause.svg';
import { ReactComponent as PlayIcon } from '../../../assets/icons/play.svg';
import { PlaybackIconFlash, ReelVideoPlayerRoot, ReelVideoSurface } from './ReelVideoPlayer.styles';

const ReelVideoPlayer = React.forwardRef(({ src, thumbnailSrc, isActive }, ref) => {
  const videoRef = useRef(null);
  const iconTimeoutRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(Boolean(isActive));
  const [showIcon, setShowIcon] = useState(false);

  const triggerIconFlash = () => {
    setShowIcon(true);
    clearTimeout(iconTimeoutRef.current);
    iconTimeoutRef.current = setTimeout(() => setShowIcon(false), 800);
  };

  const togglePlayback = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !isActive) return;

    if (video.paused) {
      await video.play().catch(() => {});
      setIsPlaying(true);
      triggerIconFlash();
      return;
    }

    video.pause();
    setIsPlaying(false);
    triggerIconFlash();
  }, [isActive]);

  const handleKeyDown = event => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    togglePlayback();
  };

  useImperativeHandle(
    ref,
    () => ({
      togglePlayback,
    }),
    [togglePlayback],
  );

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (isActive) {
      video.unmute();
      video.play().catch(() => {});
      return;
    }

    video.pause();
    video.mute();
    setIsPlaying(false);
  }, [isActive]);

  useEffect(() => {
    const video = videoRef.current;

    return () => {
      clearTimeout(iconTimeoutRef.current);
      video?.pause();
    };
  }, []);

  return (
    <ReelVideoPlayerRoot onKeyDown={handleKeyDown} tabIndex={0}>
      <ReelVideoSurface
        ref={videoRef}
        src={src}
        posterSrc={thumbnailSrc}
        autoPlay={isActive}
        muted={!isActive}
        preload={isActive ? 'auto' : 'metadata'}
        playWhenVisible={false}
        primeFrameOnLoad={false}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      {showIcon ? <PlaybackIconFlash>{isPlaying ? <PauseIcon /> : <PlayIcon />}</PlaybackIconFlash> : null}
    </ReelVideoPlayerRoot>
  );
});

ReelVideoPlayer.displayName = 'ReelVideoPlayer';

export default ReelVideoPlayer;
